<script>
  import data from "./data/seats_by_women.json";
  import {
    ticks,
    group,
    range,
    min,
    max,
    least,
    format,
    scaleLinear,
    scaleSequential,
    interpolatePurples,
    interpolateBuPu,
    interpolateRdPu,
    interpolateViridis,
  } from "d3";
  import { regressionLinear } from "d3-regression";
  import { tweened } from "svelte/motion";
  import { fade } from "svelte/transition";
  import { linear } from "svelte/easing";
  import Select from "svelte-select";
  import { getGuid } from "./util.js";
  import Line from "./Line.svelte";
  import Axis from "./Axis.svelte";

  export let step;
  export let scrollY;

  // Define state variables
  let showReference = true;

  let showRegression = false;
  $: if (showRegression) {
    tweenedXMax.set(xMaxAlt);
  } else {
    tweenedXMax.set(xMax);
  }

  let focusedKeys = ["World"];
  let selectedKey;

  let hoveredKey;
  let hoveredData;

  // Get all keys that are focused
  $: allFocusedKeys = [...focusedKeys, hoveredKey, selectedKey].filter(
    (k) => k
  );
  $: allFocusedData = allFocusedKeys.map((k) => {
    return dataGrouped.get(k).filter((d) => d["Year"] === xMax)[0];
  });

  const valueFormat = format(".1f");
  const fadeOptions = { duration: 400, ease: linear };
  const annosFadeOptions = { duration: 300 };

  // TODO: Should I filter out null entries?
  // const dataFiltered = data;
  const dataFiltered = data.filter((d) => d["Value"] !== null);

  // Retrieve list of all countries
  const keys = [...new Set(dataFiltered.map((d) => d["Country Name"]))].filter(
    (d) => d !== "World"
  );

  // Group data by country
  const keyAccessor = (d) => d["Country Name"];
  $: dataGrouped = group(dataFiltered, keyAccessor);

  // Define chart dimensions
  let width;
  let height;

  const margins = {
    marginTop: 20,
    marginRight: 100,
    marginBottom: 70,
    marginLeft: 100,
  };
  // Chart dimensions will change upon resizing
  $: dimensions = {
    width,
    height,
    ...margins,
    boundedHeight: Math.max(
      height - margins.marginTop - margins.marginBottom,
      0
    ),
    boundedWidth: Math.max(width - margins.marginLeft - margins.marginRight, 0),
  };

  // Define line chart scales
  const xAccessor = (d) => d["Year"];
  const xMin = min(dataFiltered, xAccessor);
  const xMax = max(dataFiltered, xAccessor);

  const tweenedXMax = tweened(xMax, {
    duration: 1000,
  });

  // let xMax = 2050
  $: xScale = scaleLinear()
    .domain([xMin, $tweenedXMax])
    .range([0, dimensions.boundedWidth]);
  $: xAccessorScaled = (d) => xScale(xAccessor(d));

  const yAccessor = (d) => d["Value"];
  const yMin = min(dataFiltered, yAccessor);
  const yMax = max(dataFiltered, yAccessor) + 5;
  $: yScale = scaleLinear()
    .domain([yMin, yMax])
    .range([dimensions.boundedHeight, 0]);
  $: yAccessorScaled = (d) => yScale(yAccessor(d));

  // Define color gradient
  const gradientId = getGuid();
  const colorAccessor = (d) => d["Value"];
  const colorScale = scaleSequential([0, 100], interpolateViridis);
  const colorAccessorScaled = (d) => colorScale(colorAccessor(d));
  // const colorInterpolator = colorScale.interpolator();

  // Perform regression
  const xMaxAlt = 2063;
  const regressor = regressionLinear()
    .x(xAccessor)
    .y(yAccessor)
    .domain([xMin, xMaxAlt]);
  const regressionOutput = regressor(
    dataFiltered.filter((d) => d["Country Name"] === "World")
  );
  // Compute data points to plot
  const regressionData = range(xMax, xMaxAlt + 1).map((x) => ({
    "Country Name": "World (Estimate)",
    Year: x,
    Value: regressionOutput.predict(x),
  }));

  const dataAll = [...dataFiltered, ...regressionData];
  // const dataAll = [...dataFiltered];

  // Scrolly steps
  $: {
    if (step == 0) {
      showRegression = false;
      focusedKeys = ["World"];
      // showReference = true;
    }
    if (step == 1) {
      showRegression = true;
      focusedKeys = ["World"];
      showReference = true;
    }
    if (step == 2) {
      showRegression = false;
      focusedKeys = ["World"];
      showReference = true;
    }
    if (step == 3) {
      showRegression = false;
      focusedKeys = ["World", "Rwanda", "Cuba", "United Arab Emirates"];
      showReference = true;
    }
    if (step == 4) {
      showRegression = false;
      focusedKeys = ["World", "Singapore"];
      showReference = true;
    }
    if (step == 5) {
      showRegression = false;
      focusedKeys = ["World", "Singapore"];
      showReference = false;
    }
  }
</script>

<div
  class="chart-container"
  bind:offsetWidth={width}
  bind:offsetHeight={height}
>
  {#if width && height}
    <!-- <button
      style="position: absolute;"
      on:click={() => (showRegression = !showRegression)}>test</button
    > -->
    <!-- Draw reference -->
    {#if showReference}
      <div
        transition:fade={fadeOptions}
        class="reference-rect"
        style="height: {yScale(50) + dimensions.marginTop}px"
      />
    {/if}

    <!-- Focused text annotations -->
    {#each allFocusedData as d}
      <div
        transition:fade={annosFadeOptions}
        class="hovered-text"
        style="
          color: {colorAccessorScaled(d)};
          transform: translate({xAccessorScaled(d) +
          10 +
          dimensions.marginLeft}px, {yAccessorScaled(d) +
          dimensions.marginTop}px);
        "
      >
        {keyAccessor(d)}
      </div>
    {/each}

    <!-- Tooltip -->
    {#if hoveredData}
      <div
        transition:fade={annosFadeOptions}
        class="tooltip"
        style="
        transform: translate(calc({xAccessorScaled(hoveredData) +
          dimensions.marginLeft}px - 50%), calc({yAccessorScaled(hoveredData) +
          dimensions.marginTop}px - 120%));
      "
      >
        <div
          class="tooltip-title"
          style="color: {colorAccessorScaled(hoveredData)}"
        >
          {valueFormat(yAccessor(hoveredData)) + "%"}
        </div>
        <div class="tooltip-subtitle">
          {xAccessor(hoveredData)}
        </div>
      </div>
    {/if}

    <!-- Chart title -->
    {#if scrollY > 0}
      <div
        transition:fade={fadeOptions}
        class="chart-title"
        style="
        transform: translate({dimensions.marginLeft +
          20}px, {dimensions.marginTop + 20}px);
      "
      >
        <div>Proportion of Seats (%) held by Women</div>
        <div>Over Time, <span class="light">in</span></div>
        <div class="select-input">
          <Select
            on:select={(e) => {
              selectedKey = e.detail.value;
            }}
            on:clear={() => {
              selectedKey = null;
            }}
            items={keys}
            value={selectedKey}
          />
        </div>
      </div>
    {/if}

    <!-- Chart body -->
    <svg width={dimensions.width} height={dimensions.height} overflow="visible">
      <!-- Draw reference -->
      <!-- <rect
        in:fade={fadeOptions}
        class="reference-rect"
        x={0}
        y={0}
        {width}
        height={yScale(50) + dimensions.marginTop}
      /> -->

      <!-- Main chart -->
      <g
        transform={`translate(${dimensions.marginLeft}, ${dimensions.marginTop})`}
      >
        <!-- Clip path for regression line -->
        <defs>
          <clipPath id="bounds-clip-path">
            <rect
              width={dimensions.boundedWidth}
              height={dimensions.boundedHeight}
            />
          </clipPath>
        </defs>

        <!-- Gradient -->
        <linearGradient
          id={gradientId}
          gradientUnits="userSpaceOnUse"
          x1={0}
          y1={dimensions.boundedHeight}
          x2={0}
          y2={0}
        >
          {#each ticks(0, 1, 10) as t}
            <stop offset={t} stop-color={colorScale(yMax * t)} />
          {/each}
        </linearGradient>

        <!-- Annotations -->
        <text
          transition:fade={fadeOptions}
          class="text-annotation"
          y={yScale(50)}
          dy={-10}
        >
          Gender Parity
        </text>

        <!-- Line series -->
        <g class="lines">
          {#each [...dataGrouped] as [key, d] (key)}
            <Line
              data={d}
              xAccessor={xAccessorScaled}
              yAccessor={yAccessorScaled}
              strokeWidth={allFocusedKeys.includes(key) ? 3.5 : 1.5}
              strokeOpacity={allFocusedKeys.includes(key) ? 1 : 0.3}
              stroke={allFocusedKeys.includes(key)
                ? `url(#${gradientId})`
                : "var(--grey)"}
            />
          {/each}
        </g>

        <!-- Regression line -->
        <g clip-path="url(#bounds-clip-path)">
          <Line
            data={regressionData}
            xAccessor={xAccessorScaled}
            yAccessor={yAccessorScaled}
            strokeWidth={4}
            strokeOpacity={1}
            stroke={`url(#${gradientId})`}
            strokeDasharray={"12"}
          />
        </g>

        <!-- Text annotation for focused keys -->
        <!-- {#each allFocusedData as d}
          FIXME: Use html instead?
          <text
            transition:fade={annosFadeOptions}
            class="hovered-text"
            x={xAccessorScaled(d)}
            y={yAccessorScaled(d)}
            fill={colorAccessorScaled(d)}
            dx="10"
          >
            {keyAccessor(d)}
          </text>
        {/each} -->

        <!-- Hover elements -->
        {#if hoveredData}
          <g transition:fade={annosFadeOptions}>
            <circle
              class="hovered-circle"
              transition:fade={{ duration: 200, easing: linear }}
              cx={xAccessorScaled(hoveredData)}
              cy={yAccessorScaled(hoveredData)}
              fill={colorAccessorScaled(hoveredData)}
              r="5"
            />
          </g>
        {/if}

        <!-- For mouse interactions -->
        <!-- svelte-ignore a11y-mouse-events-have-key-events -->
        <rect
          on:mousemove={(e) => {
            const bounds = e.target.getBoundingClientRect();
            const x = e.clientX - bounds.left;
            const y = e.clientY - bounds.top;

            // Find closest data point
            hoveredData = least(dataAll, (d) => {
              return Math.hypot(xAccessorScaled(d) - x, yAccessorScaled(d) - y);
            });
            // HACK
            hoveredKey =
              keyAccessor(hoveredData) === "World (Estimate)"
                ? "World"
                : keyAccessor(hoveredData);
          }}
          on:mouseout={() => {
            hoveredData = null;
            hoveredKey = null;
          }}
          on:click={(e) => {
            const bounds = e.target.getBoundingClientRect();
            const x = e.clientX - bounds.left;
            const y = e.clientY - bounds.top;

            const selectedData = least(dataAll, (d) => {
              return Math.hypot(xAccessorScaled(d) - x, yAccessorScaled(d) - y);
            });
            selectedKey = keyAccessor(selectedData);
          }}
          width={dimensions.boundedWidth}
          height={dimensions.boundedHeight}
          fill-opacity="0"
        />
        <!-- Draw axes -->
        <Axis
          {dimensions}
          dimension={"x"}
          scale={xScale}
          formatTick={(t) => t}
        />
        <Axis
          {dimensions}
          dimension={"y"}
          numberOfTicks={7}
          scale={yScale}
          drawAxisLine={false}
        />
      </g>
    </svg>
  {/if}
</div>

<style>
  .chart-container {
    position: relative;
    height: 100vh;
    max-width: 100%;
    /* background: linear-gradient(to bottom right, steelblue -100%, white 100%); */
    /* border: 1px solid lightgrey; */
    /* border-radius: 5px; */
    /* box-shadow: 1px 1px 6px #cecece; */
  }
  /* .reference-rect {
    fill: var(--light-green);
  } */
  .reference-rect {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    background: var(--light-green);
    pointer-events: none;
    z-index: -1;
  }
  .chart-title {
    position: absolute;
    font-family: var(--font-serif);
    font-size: calc(22 / 16 * 1rem);
    text-shadow: var(--white-text-shadow);
    text-align: left;
  }
  .light {
    color: var(--lightish-grey);
  }
  .select-input {
    margin-left: -16px;
    margin-top: -5px;
    --background: none;
    --border: none;
    /* --borderFocusColor: none;
    --borderHoverColor: none; */
    --inputFontSize: calc(22 / 16 * 1rem);
    --itemHoverBG: var(--light-green);
    --itemIsActiveBG: var(--light-purple);
    --itemIsActiveColor: black;
  }
  .hovered-text {
    position: absolute;
    dominant-baseline: hanging;
    font-size: calc(14 / 16 * 1rem);
    width: 50px;
    text-align: left;
    line-height: 1.1;
  }
  .text-annotation {
    fill: var(--green);
    /* dominant-baseline: auto; */
    font-size: calc(14 / 16 * 1rem);
  }
  .tooltip {
    position: absolute;
    top: 0;
    left: 0;
    padding: 6px 10px;
    background: #fff;
    text-align: center;
    border: 1px solid #ddd;
    border-radius: 5px;
    pointer-events: none;
    font-size: calc(14 / 16 * 1rem);
  }
  .tooltip-title {
    font-weight: 700;
    margin-bottom: 3px;
  }
</style>
